import { sanitizeImageMetadata } from './imageMetadata';

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

function imageType(bytes: Uint8Array): string | undefined {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (PNG_SIGNATURE.every((byte, i) => bytes[i] === byte)) {
    return 'image/png';
  }
  const text = new TextDecoder('ascii').decode(bytes);
  if (text.startsWith('RIFF') && text.slice(8, 12) === 'WEBP') {
    return 'image/webp';
  }
}

/** Remove privacy-sensitive metadata without decoding or recompressing pixels.
 * Technical EXIF values (including orientation), color and animation data remain.
 * Unsupported formats and processing failures pass through unchanged.
 * Upload availability takes priority; metadata removal is best effort, not a security boundary.
 */
export async function stripImageMetadata(file: File): Promise<File> {
  try {
    const type = imageType(
      new Uint8Array(await file.slice(0, 12).arrayBuffer())
    );
    if (!type) return file;
    const original = new Uint8Array(await file.arrayBuffer());
    const cleaned = sanitizeImageMetadata(original, type);
    if (
      original.length === cleaned.length &&
      original.every((byte, i) => byte === cleaned[i])
    )
      return file;
    return new File([cleaned as Uint8Array<ArrayBuffer>], file.name, {
      type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

/** Copy the body so retries and component state still own the original files. */
export async function prepareUploadBody(body: FormData): Promise<FormData> {
  try {
    const entries: [string, FormDataEntryValue][] = [];
    body.forEach((value, key) => entries.push([key, value]));
    const prepared = new FormData();
    // Process sequentially to bound temporary memory per multipart body.
    for (const [key, value] of entries) {
      if (typeof value === 'string') {
        prepared.append(key, value);
      } else {
        prepared.append(key, await stripImageMetadata(value), value.name);
      }
    }
    return prepared;
  } catch {
    // Even copying the multipart body must not prevent the original upload.
    return body;
  }
}
